import {
  PRISM_ANIMATION_3DROTATE,
  PRISM_ANIMATION_HOVER,
  PRISM_NOISE_ZERO_EPSILON,
  PRISM_POINTER_MAX,
  PRISM_ROTATION_HOVER_MAX_ANGLE,
  PRISM_ROTATION_ROLL_RETURN_INERTIA,
  PRISM_ROTATION_SETTLE_EPSILON,
  PRISM_TIME_STOP_EPSILON,
} from './prismConstants';
import type { PrismAnimationType, PrismRuntimeConfig } from './prismTypes';

const PRISM_WOBBLE_X = 0.67;
const PRISM_WOBBLE_Y = 0.53;
const PRISM_WOBBLE_Z = 0.31;
const PRISM_PHASE_X = Math.PI * 0.42;
const PRISM_PHASE_Z = Math.PI * 1.24;

interface PrismRotationAnimatorInput {
  animationType: PrismAnimationType;
  runtimeConfig: PrismRuntimeConfig;
  pointer: PrismPointerTracker;
}

interface PrismRotationUpdate {
  matrix: Float32Array;
  continueRAF: boolean;
}

export class PrismPointerTracker {
  private pointerX = 0;
  private pointerY = 0;
  private pointerInside = true;

  get x(): number {
    return this.pointerX;
  }

  get y(): number {
    return this.pointerY;
  }

  get inside(): boolean {
    return this.pointerInside;
  }

  move(event: PointerEvent): void {
    const windowWidth = Math.max(1, window.innerWidth);
    const windowHeight = Math.max(1, window.innerHeight);
    const centerX = windowWidth * 0.5;
    const centerY = windowHeight * 0.5;
    const nextX = (event.clientX - centerX) / (windowWidth * 0.5);
    const nextY = (event.clientY - centerY) / (windowHeight * 0.5);
    this.pointerX = Math.max(-PRISM_POINTER_MAX, Math.min(PRISM_POINTER_MAX, nextX));
    this.pointerY = Math.max(-PRISM_POINTER_MAX, Math.min(PRISM_POINTER_MAX, nextY));
    this.pointerInside = true;
  }

  leave(): void {
    this.pointerInside = false;
  }
}

export class PrismRotationAnimator {
  private readonly animationType: PrismAnimationType;
  private readonly pointer: PrismPointerTracker;
  private readonly hoverStrength: number;
  private readonly inertia: number;
  private readonly noiseIsZero: boolean;
  private readonly timeScale: number;
  private readonly wobbleX: number;
  private readonly wobbleY: number;
  private readonly wobbleZ: number;
  private readonly phaseX: number;
  private readonly phaseZ: number;
  private readonly rotationBuffer = new Float32Array(9);
  private yaw = 0;
  private pitch = 0;
  private roll = 0;
  private targetYaw = 0;
  private targetPitch = 0;

  constructor({ animationType, runtimeConfig, pointer }: PrismRotationAnimatorInput) {
    this.animationType = animationType;
    this.pointer = pointer;
    this.hoverStrength = runtimeConfig.hoverStrength;
    this.inertia = runtimeConfig.inertia;
    this.noiseIsZero = runtimeConfig.noise < PRISM_NOISE_ZERO_EPSILON;
    this.timeScale = runtimeConfig.timeScale;
    this.wobbleX = PRISM_WOBBLE_X;
    this.wobbleY = PRISM_WOBBLE_Y;
    this.wobbleZ = PRISM_WOBBLE_Z;
    this.phaseX = PRISM_PHASE_X;
    this.phaseZ = PRISM_PHASE_Z;
  }

  update(time: number): PrismRotationUpdate {
    if (this.animationType === PRISM_ANIMATION_HOVER) return this.updateHover();
    if (this.animationType === PRISM_ANIMATION_3DROTATE) return this.update3dRotate(time);
    return this.updateRotate();
  }

  private updateHover(): PrismRotationUpdate {
    const maxPitch = PRISM_ROTATION_HOVER_MAX_ANGLE * this.hoverStrength;
    const maxYaw = PRISM_ROTATION_HOVER_MAX_ANGLE * this.hoverStrength;
    this.targetYaw = (this.pointer.inside ? -this.pointer.x : 0) * maxYaw;
    this.targetPitch = (this.pointer.inside ? this.pointer.y : 0) * maxPitch;
    this.yaw = lerp(this.yaw, this.targetYaw, this.inertia);
    this.pitch = lerp(this.pitch, this.targetPitch, this.inertia);
    this.roll = lerp(this.roll, 0, PRISM_ROTATION_ROLL_RETURN_INERTIA);

    return {
      matrix: setMat3FromEuler(this.yaw, this.pitch, this.roll, this.rotationBuffer),
      continueRAF: this.shouldContinueHover(),
    };
  }

  private update3dRotate(time: number): PrismRotationUpdate {
    const scaledTime = time * this.timeScale;
    this.yaw = scaledTime * this.wobbleY;
    this.pitch = Math.sin(scaledTime * this.wobbleX + this.phaseX) * PRISM_ROTATION_HOVER_MAX_ANGLE;
    this.roll = Math.sin(scaledTime * this.wobbleZ + this.phaseZ) * 0.5;

    return {
      matrix: setMat3FromEuler(this.yaw, this.pitch, this.roll, this.rotationBuffer),
      continueRAF: this.timeScale >= PRISM_TIME_STOP_EPSILON,
    };
  }

  private updateRotate(): PrismRotationUpdate {
    return {
      matrix: setIdentityMat3(this.rotationBuffer),
      continueRAF: this.timeScale >= PRISM_TIME_STOP_EPSILON,
    };
  }

  private shouldContinueHover(): boolean {
    if (!this.noiseIsZero) return true;

    const settled =
      Math.abs(this.yaw - this.targetYaw) < PRISM_ROTATION_SETTLE_EPSILON &&
      Math.abs(this.pitch - this.targetPitch) < PRISM_ROTATION_SETTLE_EPSILON &&
      Math.abs(this.roll) < PRISM_ROTATION_SETTLE_EPSILON;

    return !settled;
  }
}

function lerp(start: number, end: number, amount: number): number {
  return start + (end - start) * amount;
}

function setIdentityMat3(output: Float32Array): Float32Array {
  output[0] = 1;
  output[1] = 0;
  output[2] = 0;
  output[3] = 0;
  output[4] = 1;
  output[5] = 0;
  output[6] = 0;
  output[7] = 0;
  output[8] = 1;
  return output;
}

function setMat3FromEuler(yawY: number, pitchX: number, rollZ: number, output: Float32Array): Float32Array {
  const cy = Math.cos(yawY);
  const sy = Math.sin(yawY);
  const cx = Math.cos(pitchX);
  const sx = Math.sin(pitchX);
  const cz = Math.cos(rollZ);
  const sz = Math.sin(rollZ);
  const r00 = cy * cz + sy * sx * sz;
  const r01 = -cy * sz + sy * sx * cz;
  const r02 = sy * cx;

  const r10 = cx * sz;
  const r11 = cx * cz;
  const r12 = -sx;

  const r20 = -sy * cz + cy * sx * sz;
  const r21 = sy * sz + cy * sx * cz;
  const r22 = cy * cx;

  output[0] = r00;
  output[1] = r10;
  output[2] = r20;
  output[3] = r01;
  output[4] = r11;
  output[5] = r21;
  output[6] = r02;
  output[7] = r12;
  output[8] = r22;
  return output;
}
